import { Editor } from "@monaco-editor/react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import type {
  MagicDefinition,
  MagicDefinitionCreateInput,
  MagicDefinitionUpdateInput,
} from "../types/magic.ts";

interface MagicDefinitionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    data: MagicDefinitionCreateInput | MagicDefinitionUpdateInput,
    upstreamChanges?: { addTo: string[]; removeFrom: string[] },
  ) => void;
  definition?: MagicDefinition | null;
  existingDefinitions?: MagicDefinition[];
}

const MagicDefinitionDialog: React.FC<MagicDefinitionDialogProps> = ({
  open,
  onClose,
  onSave,
  definition,
  existingDefinitions = [],
}) => {
  const isEdit = !!definition;

  const [formData, setFormData] = useState<{
    name: string;
    x_id: string;
    type: "generic" | "trigger" | "hook";
    description: string;
    filter: string;
    validator: string;
    dep_ids: string[];
    upstream_ids: string[]; // definitions that depend on this one
    timeout_ms: number | null;
  }>({
    name: "",
    x_id: "",
    type: "generic",
    description: "",
    filter: "",
    validator: "",
    dep_ids: [],
    upstream_ids: [],
    timeout_ms: null,
  });

  useEffect(() => {
    if (definition) {
      // Find definitions that have this definition as a dependency (upstream)
      const upstreamDefs = existingDefinitions
        .filter((def) => def.dep_ids?.includes(definition.x_id))
        .map((def) => def.x_id);

      setFormData({
        name: definition.name,
        x_id: definition.x_id,
        type: definition.type as "generic" | "trigger" | "hook",
        description: definition.description || "",
        filter: definition.filter || "",
        validator: definition.validator || "",
        dep_ids: definition.dep_ids || [],
        upstream_ids: upstreamDefs,
        timeout_ms: definition.conditions?.[0]?.timeout_ms ?? null,
      });
    } else {
      setFormData({
        name: "",
        x_id: "",
        type: "generic",
        description: "",
        filter: "",
        validator: "",
        dep_ids: [],
        upstream_ids: [],
        timeout_ms: null,
      });
    }
  }, [definition, existingDefinitions]);

  const handleSave = () => {
    const payload: MagicDefinitionCreateInput | MagicDefinitionUpdateInput = {
      name: formData.name,
      x_id: formData.x_id,
      type: formData.type,
      description: formData.description || null,
      filter: formData.filter || null,
      validator: formData.validator || null,
      dep_ids: formData.dep_ids.length > 0 ? formData.dep_ids : null,
      conditions: formData.timeout_ms !== null ? [{ timeout_ms: formData.timeout_ms }] : null,
    };

    // Calculate upstream changes
    let upstreamChanges: { addTo: string[]; removeFrom: string[] } | undefined;

    if (definition) {
      // When editing: calculate diff from original upstream
      const originalUpstream = existingDefinitions
        .filter((def) => def.dep_ids?.includes(definition.x_id))
        .map((def) => def.x_id);

      const addTo = formData.upstream_ids.filter((id) => !originalUpstream.includes(id));
      const removeFrom = originalUpstream.filter((id) => !formData.upstream_ids.includes(id));

      if (addTo.length > 0 || removeFrom.length > 0) {
        upstreamChanges = { addTo, removeFrom };
      }
    } else {
      // When creating: all selected upstream_ids should be added
      if (formData.upstream_ids.length > 0) {
        upstreamChanges = {
          addTo: formData.upstream_ids,
          removeFrom: [],
        };
      }
    }

    onSave(payload, upstreamChanges);
  };

  // Any definition can be a dependency
  const availableDepIds = existingDefinitions
    .filter((def) => def.x_id !== formData.x_id)
    .map((def) => ({ x_id: def.x_id, source: def.source }));

  // Any definition can be selected as upstream
  // But only manual definitions will actually be modifiable (validated in backend)
  const availableUpstreamIds = existingDefinitions
    .filter((def) => def.x_id !== formData.x_id)
    .map((def) => ({ x_id: def.x_id, source: def.source }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Magic Definition" : "Create Magic Definition"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              helperText="The name of the magic definition (used for grouping)"
            />
            <TextField
              label="X ID"
              value={formData.x_id}
              onChange={(e) => setFormData({ ...formData, x_id: e.target.value })}
              fullWidth
              required
              helperText="Unique identifier for this specific definition"
            />
          </Stack>
          <TextField
            select
            label="Type"
            value={formData.type}
            onChange={(e) =>
              setFormData({
                ...formData,
                type: e.target.value as "generic" | "trigger" | "hook",
              })
            }
            fullWidth
            required
          >
            <MenuItem value="generic">Generic</MenuItem>
            <MenuItem value="trigger">Trigger</MenuItem>
            <MenuItem value="hook">Hook</MenuItem>
          </TextField>
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />

          <Stack direction="row" spacing={2}>
            <Autocomplete
              multiple
              fullWidth
              options={availableDepIds.map((d) => d.x_id)}
              value={formData.dep_ids}
              onChange={(_, newValue) => {
                // Auto-populate name with first dependency name when creating
                if (
                  !definition &&
                  !formData.name &&
                  newValue.length > 0 &&
                  formData.dep_ids.length === 0
                ) {
                  const firstDep = existingDefinitions.find((def) => def.x_id === newValue[0]);
                  if (firstDep) {
                    setFormData({
                      ...formData,
                      dep_ids: newValue,
                      name: firstDep.name,
                    });
                    return;
                  }
                }
                setFormData({ ...formData, dep_ids: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dependencies (this depends on)"
                  helperText="Select any definitions this one depends on"
                />
              )}
              renderOption={(props, option) => {
                const def = availableDepIds.find((d) => d.x_id === option);
                return (
                  <li {...props}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%" }}
                    >
                      <span>{option}</span>
                      {def?.source === "code" && (
                        <Chip label="code" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const def = availableDepIds.find((d) => d.x_id === option);
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      variant={def?.source === "code" ? "outlined" : "filled"}
                    />
                  );
                })
              }
            />
            <Autocomplete
              multiple
              fullWidth
              options={availableUpstreamIds.map((d) => d.x_id)}
              value={formData.upstream_ids}
              onChange={(_, newValue) => setFormData({ ...formData, upstream_ids: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Upstream (defs that depend on this)"
                  helperText="Select definitions to add this as a dependency"
                />
              )}
              renderOption={(props, option) => {
                const def = availableUpstreamIds.find((d) => d.x_id === option);
                return (
                  <li {...props}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%" }}
                    >
                      <span>{option}</span>
                      {def?.source === "code" && (
                        <Chip label="code" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const def = availableUpstreamIds.find((d) => d.x_id === option);
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      variant={def?.source === "code" ? "outlined" : "filled"}
                    />
                  );
                })
              }
            />
          </Stack>

          <TextField
            label="Timeout (ms)"
            type="number"
            value={formData.timeout_ms ?? ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                timeout_ms: e.target.value ? parseInt(e.target.value, 10) : null,
              })
            }
            fullWidth
            helperText="Optional timeout in milliseconds for this definition"
          />

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle2">Filter (Python expression)</Typography>
              <Typography variant="caption" color="text.secondary">
                Available: <code>data</code>, <code>ctx.data</code>. Return true/false.
              </Typography>
            </Stack>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Editor
                height="100px"
                defaultLanguage="python"
                value={formData.filter}
                onChange={(value) => setFormData({ ...formData, filter: value || "" })}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: "on",
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle2">Validator (Python expression)</Typography>
              <Typography variant="caption" color="text.secondary">
                Available: <code>data</code>, <code>ctx.data</code>. Return true/false.
              </Typography>
            </Stack>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Editor
                height="100px"
                defaultLanguage="python"
                value={formData.validator}
                onChange={(value) => setFormData({ ...formData, validator: value || "" })}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: "on",
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.name || !formData.x_id}
        >
          {isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MagicDefinitionDialog;
